import React, { useEffect } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { AnimatedLoading } from '@actual-app/components/icons/AnimatedLoading';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import { weekStart } from '@actual-app/core/shared/mtxbudget';
import { tsToRelativeTime } from '@actual-app/core/shared/util';

import { useSyncAndDownloadMutation } from '#accounts';
import { sync } from '#app/appSlice';
import { Link } from '#components/common/Link';
import { useLocale } from '#hooks/useLocale';
import { useDispatch } from '#redux';

import {
  getRecalcul,
  RECALCUL_STEPS,
  setRecalcul,
  useDismissed,
  useRecalcul,
  useUnreadAccounts,
} from './recalcul';
import { useMtxbudget } from './useMtxbudget';
import { PARCOURS_PAYS, useParcours } from './useParcours';

/**
 * « Tout recalculer », at the top of every MTXbudget screen: syncs with the
 * server, reads the banks again, recomputes the thirteen pays and replays the
 * checks of the calculation. Gives the three parts of the header it lives in.
 */
export function useRecalculer(): {
  subtitle: ReactNode;
  button: ReactNode;
  below: ReactNode;
} {
  const { t } = useTranslation();
  const locale = useLocale();
  const dispatch = useDispatch();
  const syncAndDownload = useSyncAndDownloadMutation();
  const recalcul = useRecalcul();
  const unread = useUnreadAccounts();
  const { config, isLoading } = useMtxbudget();
  const parcours = useParcours();

  // A closed message comes back when it has something new to say: another
  // account left unread, a new week, another recalculation offline.
  const [unreadClosed, closeUnread] = useDismissed(
    'unread',
    [
      weekStart(monthUtils.currentDay()),
      ...unread.map(account => account.id).sort(),
    ].join(' '),
  );
  const [offlineClosed, closeOffline] = useDismissed(
    'offline',
    String(recalcul.doneAt),
  );

  // The walk asked for has been computed: the recalculation is over. The
  // first walk of the session counts as one, made from the data of opening.
  const computed =
    parcours.status !== 'loading' &&
    parcours.generation === recalcul.generation;
  const nothingToCompute = !isLoading && !config;
  const ok = parcours.checks.filter(check => check.ok).length;
  const total = parcours.checks.length;
  useEffect(() => {
    const current = getRecalcul();
    if (current.step === 3 && (computed || nothingToCompute)) {
      const now = Date.now();
      setRecalcul({
        step: null,
        doneAt: now,
        dataAt: current.outcome === 'done' ? now : current.dataAt,
        checks: computed ? { ok, total } : null,
      });
    } else if (current.step === null && current.doneAt === null && computed) {
      const now = Date.now();
      setRecalcul({ doneAt: now, dataAt: now, checks: { ok, total } });
    }
  }, [computed, nothingToCompute, ok, total, recalcul.step]);

  async function run() {
    setRecalcul({ step: 1, outcome: null });
    let outcome: 'done' | 'offline' = 'done';
    try {
      if (!navigator.onLine) {
        throw new Error('offline');
      }
      const synced = await dispatch(sync()).unwrap();
      if (synced && 'error' in synced && synced.error) {
        throw new Error('sync');
      }
      setRecalcul({ step: 2 });
      const read = await syncAndDownload.mutateAsync({});
      if (typeof read === 'object' && read.error) {
        throw new Error('banks');
      }
    } catch {
      // The pays are recomputed all the same, from the data already loaded.
      outcome = 'offline';
    }
    setRecalcul({
      step: 3,
      outcome,
      generation: getRecalcul().generation + 1,
    });
  }

  const ago = (stamp: number | null) =>
    stamp === null
      ? ''
      : Date.now() - stamp < 60_000
        ? t("à l'instant")
        : tsToRelativeTime(String(stamp), locale);
  const running = recalcul.step !== null;
  const wrong = recalcul.checks && recalcul.checks.ok < recalcul.checks.total;
  const verdict = recalcul.checks
    ? t('{{ok}} contrôles sur {{total}}', recalcul.checks)
    : '';

  let subtitle: { text: string; color: string } | null = null;
  if (recalcul.step !== null) {
    subtitle = {
      text: t('Étape {{step}} sur {{steps}} : {{what}}…', {
        step: recalcul.step,
        steps: RECALCUL_STEPS,
        what: [
          t('synchronisation avec le serveur'),
          t('lecture des banques'),
          t('calcul des {{pays}} paies', { pays: PARCOURS_PAYS }),
        ][recalcul.step - 1],
      }),
      color: theme.pageTextSubdued,
    };
  } else if (recalcul.doneAt !== null) {
    if (recalcul.outcome === 'offline') {
      subtitle = {
        text: t('Banques non relues · données {{when}}', {
          when: ago(recalcul.dataAt),
        }),
        color: theme.errorText,
      };
    } else if (wrong) {
      subtitle = {
        text: t('Recalculé {{when}} · {{verdict}}', {
          when: ago(recalcul.doneAt),
          verdict,
        }),
        color: theme.errorText,
      };
    } else if (recalcul.outcome === 'done' && unread.length > 0) {
      subtitle = {
        text:
          unread.length === 1
            ? t('Recalculé {{when}} · 1 compte non lu', {
                when: ago(recalcul.doneAt),
              })
            : t('Recalculé {{when}} · {{count}} comptes non lus', {
                when: ago(recalcul.doneAt),
                count: unread.length,
              }),
        color: theme.warningText,
      };
    } else if (recalcul.outcome === 'done') {
      subtitle = {
        text: t('Recalculé {{when}} · {{verdict}}', {
          when: ago(recalcul.doneAt),
          verdict,
        }),
        color: theme.noticeTextLight,
      };
    } else {
      subtitle = {
        text: t('Recalculé {{when}}', { when: ago(recalcul.doneAt) }),
        color: theme.pageTextSubdued,
      };
    }
  }

  const button = (
    <button
      type="button"
      disabled={running}
      aria-label={running ? t('Recalcul en cours') : t('Tout recalculer')}
      onClick={() => void run()}
      style={{
        flexShrink: 0,
        minHeight: 44,
        padding: '0 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        borderRadius: 22,
        border: `1px solid ${running ? theme.tableBorder : theme.buttonPrimaryBackground}`,
        backgroundColor: running
          ? theme.tableBackground
          : theme.mobileHeaderBackground,
        color: running ? theme.pageTextSubdued : theme.pageTextDark,
        font: 'inherit',
        fontSize: 13,
        fontWeight: 600,
        cursor: running ? 'default' : 'pointer',
      }}
    >
      {running ? (
        <AnimatedLoading width={18} height={18} />
      ) : (
        <svg
          aria-hidden="true"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke={theme.mobileNavItemSelected}
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 11a8 8 0 0 0-14.5-4.5L4 8" />
          <path d="M4 4v4h4" />
          <path d="M4 13a8 8 0 0 0 14.5 4.5L20 16" />
          <path d="M20 20v-4h-4" />
        </svg>
      )}
      {running ? t('Calcul…') : t('Recalculer')}
    </button>
  );

  const box = (color: string, children: ReactNode, close: () => void) => (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        margin: '6px 12px 0',
        padding: '6px 4px 6px 12px',
        borderRadius: 4,
        border: `1px solid ${color}`,
        backgroundColor: theme.tableBackground,
      }}
    >
      {children}
      <button
        type="button"
        aria-label={t('Fermer le message')}
        onClick={close}
        style={{
          width: 44,
          height: 44,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 0,
          borderRadius: 22,
          backgroundColor: 'transparent',
          color: theme.pageTextSubdued,
          cursor: 'pointer',
        }}
      >
        <svg
          aria-hidden="true"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke={theme.pageTextSubdued}
          strokeWidth="2.2"
          strokeLinecap="round"
        >
          <path d="M6 6l12 12" />
          <path d="M18 6L6 18" />
        </svg>
      </button>
    </View>
  );

  let below: ReactNode = null;
  if (recalcul.step !== null) {
    below = (
      <View style={{ gap: 6, paddingTop: 6 }}>
        <View
          role="progressbar"
          aria-label={t('Avancement du recalcul')}
          aria-valuemin={0}
          aria-valuemax={RECALCUL_STEPS}
          aria-valuenow={recalcul.step}
          style={{
            margin: '0 12px',
            height: 4,
            borderRadius: 2,
            backgroundColor: theme.tableBorder,
          }}
        >
          <View
            style={{
              width: `${(recalcul.step / RECALCUL_STEPS) * 100}%`,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.buttonPrimaryBackground,
            }}
          />
        </View>
        <Text
          style={{
            padding: '0 16px',
            fontSize: 12,
            color: theme.pageTextSubdued,
          }}
        >
          <Trans>
            La lecture des banques peut prendre une minute. L'écran reste
            utilisable.
          </Trans>
        </Text>
      </View>
    );
  } else if (recalcul.outcome === 'offline' && !offlineClosed) {
    below = box(
      theme.errorText,
      <Text role="alert" style={{ flex: 1, fontSize: 13 }}>
        {recalcul.checks
          ? t(
              'Le serveur ne répond pas. Les {{pays}} paies ont été recalculées avec les données déjà chargées : {{verdict}}. Réessayez quand le réseau revient.',
              { pays: PARCOURS_PAYS, verdict },
            )
          : t('Le serveur ne répond pas. Réessayez quand le réseau revient.')}
      </Text>,
      closeOffline,
    );
  } else if (
    recalcul.outcome === 'done' &&
    unread.length > 0 &&
    !unreadClosed
  ) {
    below = box(
      theme.warningText,
      <>
        <Text style={{ flex: 1, fontSize: 13 }}>
          {unread
            .map(account =>
              account.lastSync
                ? t(
                    "{{name}} : {{reason}}. Son solde a été lu {{when}} ; le calcul l'utilise tel quel.",
                    {
                      name: account.name,
                      reason: account.reason,
                      when: tsToRelativeTime(account.lastSync, locale),
                    },
                  )
                : t(
                    '{{name}} : {{reason}}. Le calcul utilise son dernier solde connu.',
                    { name: account.name, reason: account.reason },
                  ),
            )
            .join(' ')}
        </Text>
        <Link
          variant="internal"
          to="/etat"
          style={{
            minHeight: 44,
            padding: '0 4px',
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          <Trans>Voir dans État</Trans>
        </Link>
      </>,
      closeUnread,
    );
  }

  return {
    subtitle: subtitle && (
      <Text style={{ fontSize: 12, color: subtitle.color }}>
        {subtitle.text}
      </Text>
    ),
    button,
    below,
  };
}
